// ---------------------------------------------------------------------------
// Homefront v3 — subplot grid foundation
// ---------------------------------------------------------------------------
//
// The homefront is one continuous 21×15 field of subplots (NOT 35 independent
// plots). Each of the legacy 35 plots (7 cols × 5 rows) subdivides into a 3×3
// block of subplots, giving 21 columns × 15 rows = 315 subplots total.
//
// Coordinate layers:
//   plot      (col 0–6,  row 0–4)   — legacy plot / corridor column mapping
//   local     (lc 0–2,   lr 0–2)    — a subplot's position WITHIN its plot
//   global    (sc 0–20,  sr 0–14)   — the subplot's cell in the continuous field
//   flat      (s 0–314)             — column-major: s = sc * SUB_ROWS + sr
//
//   sc = col * SUB_PER_PLOT + lc      col = floor(sc / SUB_PER_PLOT)
//   sr = row * SUB_PER_PLOT + lr      row = floor(sr / SUB_PER_PLOT)
//
// The corridor column a subplot answers to is floor(sc / 3): crossing a plot
// OR corridor boundary is just moving one cell in the continuous field, so
// adjacency/impact/overflow need no special-casing at those seams.
//
// Flat index mirrors the legacy `col * 5 + row` (column-major) so the two
// systems read the same way: outer loop columns, inner loop rows (row 0 =
// closest to the corridor).

export const PLOT_COLS = 7
export const PLOT_ROWS = 5
export const SUB_PER_PLOT = 3
export const SUB_COLS = PLOT_COLS * SUB_PER_PLOT   // 21
export const SUB_ROWS = PLOT_ROWS * SUB_PER_PLOT   // 15
export const SUBPLOT_COUNT = SUB_COLS * SUB_ROWS   // 315

// Every subplot is 1 HP — developed, empty, or structure footprint alike. There
// are no shared HP pools or armor shares: a structure's cells are destroyed one
// apiece like any other subplot. A structure dies only when its LAST cell
// does, and only those full kills score (see structureKills.js) — destroyed
// cells on their own count for nothing.

// ── Coordinate conversions ──────────────────────────────────────────────────

export function subIndex(sc, sr) {
    return sc * SUB_ROWS + sr
}

export function subCoords(s) {
    return { sc: Math.floor(s / SUB_ROWS), sr: s % SUB_ROWS }
}

// Global (sc,sr) → owning plot (col,row) and the subplot's local position.
export function plotOf(s) {
    const { sc, sr } = subCoords(s)
    return {
        col: Math.floor(sc / SUB_PER_PLOT),
        row: Math.floor(sr / SUB_PER_PLOT),
        lc: sc % SUB_PER_PLOT,
        lr: sr % SUB_PER_PLOT,
    }
}

// The corridor column (0–6) a subplot sits under.
export function battlefieldColumn(s) {
    return Math.floor(subCoords(s).sc / SUB_PER_PLOT)
}

// plot (col,row) + local (lc,lr) → flat subplot index.
export function fromPlotSub(col, row, lc, lr) {
    return subIndex(col * SUB_PER_PLOT + lc, row * SUB_PER_PLOT + lr)
}

// All 9 flat subplot indices of a plot, in local column-major order.
export function plotSubIndices(col, row) {
    const out = []
    for (let lc = 0; lc < SUB_PER_PLOT; lc++) {
        for (let lr = 0; lr < SUB_PER_PLOT; lr++) {
            out.push(fromPlotSub(col, row, lc, lr))
        }
    }
    return out
}

const inBounds = (sc, sr) => sc >= 0 && sc < SUB_COLS && sr >= 0 && sr < SUB_ROWS

// ── Adjacency & geometry ─────────────────────────────────────────────────────

// 4-cardinal neighbours (N/S/E/W). Used by same-resource networks and by
// overflow contiguity — both flow only through orthogonally-touching cells.
export function cardinalNeighbors(s) {
    const { sc, sr } = subCoords(s)
    const out = []
    if (sr > 0)            out.push(subIndex(sc, sr - 1)) // toward corridor
    if (sr < SUB_ROWS - 1) out.push(subIndex(sc, sr + 1)) // away from corridor
    if (sc > 0)            out.push(subIndex(sc - 1, sr)) // west
    if (sc < SUB_COLS - 1) out.push(subIndex(sc + 1, sr)) // east
    return out
}

// 8 surrounding neighbours (cardinal + diagonal), excluding the cell itself.
export function boxNeighbors(s) {
    const { sc, sr } = subCoords(s)
    const out = []
    for (let dc = -1; dc <= 1; dc++) {
        for (let dr = -1; dr <= 1; dr++) {
            if (dc === 0 && dr === 0) continue
            if (inBounds(sc + dc, sr + dr)) out.push(subIndex(sc + dc, sr + dr))
        }
    }
    return out
}

// The 3×3 impact zone centred on an aim cell (the aim cell + its 8-box),
// clipped at the field edges. Crosses plot/corridor seams freely.
export function impactZone(aimS) {
    const { sc, sr } = subCoords(aimS)
    const out = []
    for (let dc = -1; dc <= 1; dc++) {
        for (let dr = -1; dr <= 1; dr++) {
            if (inBounds(sc + dc, sr + dr)) out.push(subIndex(sc + dc, sr + dr))
        }
    }
    return out
}

// Squared Euclidean distance between two subplots (ordering metric for
// overflow's nearest-first spread — squared avoids sqrt while preserving ties).
export function dist2(a, b) {
    const A = subCoords(a), B = subCoords(b)
    const dc = A.sc - B.sc, dr = A.sr - B.sr
    return dc * dc + dr * dr
}

// Chebyshev (chessboard) distance — the metric for rocket coverage rings.
export function chebyshev(a, b) {
    const A = subCoords(a), B = subCoords(b)
    return Math.max(Math.abs(A.sc - B.sc), Math.abs(A.sr - B.sr))
}

// All subplots within Chebyshev distance `radius` of ANY cell in `footprint`
// (the origin cells included). Used for rocket coverage: a broad box ring
// around the host structure, crossing plot/corridor boundaries freely.
export function coverageArea(footprint, radius) {
    const covered = new Set()
    for (const f of footprint) {
        const { sc, sr } = subCoords(f)
        for (let dc = -radius; dc <= radius; dc++) {
            for (let dr = -radius; dr <= radius; dr++) {
                if (inBounds(sc + dc, sr + dr)) covered.add(subIndex(sc + dc, sr + dr))
            }
        }
    }
    return covered
}

// The 21-subplot "plus-cross" a single rocket defends, centred on its own cell:
// the 3×3 box around it (9 cells) plus a 3-wide arm one step beyond each face
// (N/S/E/W) — geometrically a 5×5 box with its 4 corners removed. Clipped at the
// field edges; crosses plot/corridor seams freely.
export function rocketCrossCoverage(cell) {
    const { sc, sr } = subCoords(cell)
    const out = new Set()
    for (let dc = -2; dc <= 2; dc++) {
        for (let dr = -2; dr <= 2; dr++) {
            if (Math.abs(dc) === 2 && Math.abs(dr) === 2) continue  // drop the 4 corners
            if (inBounds(sc + dc, sr + dr)) out.add(subIndex(sc + dc, sr + dr))
        }
    }
    return out
}

// ── Model factories ──────────────────────────────────────────────────────────
//
// A homefront is a plain-JSON container so it round-trips through Firebase:
//   {
//     subplots:  Subplot[315],
//     structures: { [id]: { id, footprint: number[] } },
//     broods:     { [id]: { id, footprint, corridor, underConstruction, egg } },
//                 // in-war Broods (subplotBases.js). A Brood is not a
//                 // structure: it never scores, pays or hosts anything.
//     intelLetters: { [structureId]: letter },  // optional — the ATTACKER's
//                    // per-war tag ledger for THIS board's structures: each
//                    // structure gets a letter (A, B, C…) the first time a
//                    // stamp references it, in first-contact order. Letters
//                    // are never reused or reassigned within a war. Every
//                    // stamp on this board was written by the same attacker
//                    // (the board owner's opponent), so one ledger per
//                    // homefront IS "per war, per attacker".
//   }
//
// Subplot: {
//   resourceType: string|null,   // land resource, or null = empty/undeveloped
//   height:       int >= 0,      // stored, economically inert this pass (TODO: strata economics)
//   hp:           int >= 0,      // current HP (1 default; structure cell = its 9-HP share)
//   maxHp:        int >= 1,      // for per-cell HP rendering
//   destroyed:    bool,
//   structureId:  string|null,   // which structure occupies this cell
//   broodId:      string|null,   // which Brood occupies this cell
//   revealed:     bool,          // attacker has revealed this cell
//   scanned:      bool|'aged',   // revealed by a surveillance drone (renders as
//                                // intel, distinct from crater damage; also the
//                                // defender's permanent "this leaked" marker).
//                                // true = this round's snapshot (purple frame);
//                                // 'aged' (still truthy) = older — wash only
//                                // (see subplotSurveillance.ageDroneScans)
//   distance:     number|null,   // Chebyshev distance stamped at a crater's aim
//                                // cell (or a drone's target cell) to the
//                                // nearest surviving enemy structure at stamp
//                                // time. Written once, NEVER updated or
//                                // removed (legacy field name — the tagged
//                                // stamp's number half)
//   distanceLetter: string|null, // which structure the distance refers to:
//                                // the attacker's per-war letter for it (see
//                                // intelLetters). Identity only — carries no
//                                // other information about the structure
//   distanceDead: bool,          // the lettered structure has since been
//                                // fully destroyed — the stamp stays, greyed
//   impact:       bool,          // this cell was a crater's aim — keeps the
//                                // dark impact fill even after the bearing
//                                // number expires
// }

export function makeSubplot({ resourceType = null, height = 0 } = {}) {
    return {
        resourceType,
        height,
        hp: 1,
        maxHp: 1,
        destroyed: false,
        structureId: null,
        broodId: null,
        revealed: false,
        scanned: false,
        distance: null,
        distanceLetter: null,
        distanceDead: false,
        impact: false,
    }
}

// Creates a 315-subplot homefront from a builder callback.
// `describe(col, row, lc, lr)` returns { resourceType, height } (or a bare
// resourceType string, or null/undefined for empty) for each subplot.
export function makeHomefront(describe) {
    const subplots = new Array(SUBPLOT_COUNT)
    for (let col = 0; col < PLOT_COLS; col++) {
        for (let row = 0; row < PLOT_ROWS; row++) {
            for (let lc = 0; lc < SUB_PER_PLOT; lc++) {
                for (let lr = 0; lr < SUB_PER_PLOT; lr++) {
                    const desc = describe ? describe(col, row, lc, lr) : null
                    const opts = typeof desc === 'string' ? { resourceType: desc } : (desc || {})
                    subplots[fromPlotSub(col, row, lc, lr)] = makeSubplot(opts)
                }
            }
        }
    }
    return { subplots, structures: {}, broods: {} }
}

// Places a structure spanning `footprint` (array of flat subplot indices) onto
// a homefront, mutating it in place and returning it. Every footprint cell keeps
// its default 1 HP (structures carry no extra HP); this only stamps structureId.
// Throws if the footprint is empty or a cell is reused.
export function placeStructure(homefront, id, footprint) {
    if (!footprint || footprint.length === 0) {
        throw new Error(`Structure ${id} needs a non-empty footprint.`)
    }
    footprint.forEach((s) => {
        const cell = homefront.subplots[s]
        if (!cell) throw new Error(`Structure ${id}: subplot ${s} out of range.`)
        if (cell.structureId != null) {
            throw new Error(`Structure ${id}: subplot ${s} already holds ${cell.structureId}.`)
        }
        cell.structureId = id
    })
    // `rockets` is the authoritative count (coverage/interception read it).
    // `rocketCells` records WHICH footprint subplots host a rocket, for rendering
    // each rocket on its own cell (a structure holds at most one per subplot).
    homefront.structures[id] = { id, footprint: [...footprint], rockets: 0, rocketCells: [] }
    return homefront
}

// The (live-or-dead) footprint cells of a structure.
export function structureCells(homefront, id) {
    return homefront.structures[id]?.footprint ?? []
}

// The display "center" of a footprint — the middle cell of a 3-cell line, the
// cell itself for a single. Hosts the structure's resource-assignment badge.
export function footprintCenter(footprint) {
    return footprint[Math.floor((footprint.length - 1) / 2)]
}

// A structure is destroyed only when EVERY subplot it spans is destroyed.
// A partially-damaged structure still stands (persists within the war).
// (Broods die by their own rule: subplotBases.isBroodDead.)
export function isStructureDestroyed(homefront, id) {
    const footprint = structureCells(homefront, id)
    if (footprint.length === 0) return false
    return footprint.every(s => !!homefront.subplots[s]?.destroyed)
}

// Flat indices of all subplots currently occupied by a live (not-fully-destroyed)
// structure. Used by distance intel (structure hunting).
export function liveStructureCells(homefront) {
    const out = []
    for (let s = 0; s < homefront.subplots.length; s++) {
        const cell = homefront.subplots[s]
        if (cell && cell.structureId != null && !cell.destroyed) out.push(s)
    }
    return out
}

// Deep-clones a homefront (subplots + structures + Broods) for pure/immutable
// updates.
export function cloneHomefront(homefront) {
    const clone = {
        subplots: homefront.subplots.map(p => ({ ...p })),
        structures: Object.fromEntries(
            // Spread first so every field survives, then re-materialize the
            // arrays.
            Object.entries(homefront.structures).map(([id, st]) => [id, { ...st, id: st.id, footprint: [...st.footprint], rockets: st.rockets || 0, rocketCells: [...(st.rocketCells || [])] }])
        ),
        broods: Object.fromEntries(Object.entries(homefront.broods ?? {}).map(([id, b]) => [id, { ...b, footprint: [...b.footprint] }])),
    }
    // Authored Port cell (Roads v0.1) — optional, carried through when present.
    if (homefront.port != null) clone.port = homefront.port
    // The attacker's intel-letter ledger — optional, carried through when present.
    if (homefront.intelLetters) clone.intelLetters = { ...homefront.intelLetters }
    return clone
}

// Whether something stands on a cell: a structure or a Brood (display code
// draws both as the cell's occupant; the rules keep them apart).
export const isOccupied = (cell) => cell?.structureId != null || cell?.broodId != null

// The structure or Brood standing on a cell, or null.
export function occupantOf(homefront, cell) {
    if (cell?.structureId != null) return homefront?.structures?.[cell.structureId] ?? null
    if (cell?.broodId != null) return homefront?.broods?.[cell.broodId] ?? null
    return null
}

// Whether a Hive still keeps its Broods in `structures` (saved before v8).
export function hasLegacyBroods(homefront) {
    return !!homefront && (!homefront.broods || Object.values(homefront.structures ?? {}).some(st => st?.type === 'base'))
}

// Hives saved before rules v8 kept Broods in `structures` (type 'base') and
// marked their cells with structureId. Moves them to `broods`/broodId, in
// place, and returns the Hive. A Hive already in the new form is untouched.
export function migrateBroods(homefront) {
    if (!homefront) return homefront
    if (!homefront.broods) homefront.broods = {}
    for (const id of Object.keys(homefront.structures ?? {})) {
        const st = homefront.structures[id]
        if (st?.type !== 'base') continue
        const { type, rockets, rocketCells, ...brood } = st  // eslint-disable-line no-unused-vars
        homefront.broods[id] = brood
        delete homefront.structures[id]
        for (const s of st.footprint) {
            const cell = homefront.subplots[s]
            if (cell?.structureId === id) { cell.structureId = null; cell.broodId = id }
        }
    }
    return homefront
}
